import {
	Typography,
	Box,
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	styled,
	Grid,
	InputLabel,
} from '@mui/material';
import { FC, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AddOn, DriverExcess, SelectedAddOn } from '@acorn-portal/shared/src/services/renewalsApi';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatCurrency, getDocument } from '@acorn-portal/shared/src/utils';
import Dropdown from '@acorn-portal/shared/src/components/dropdown';
import { addonDocumentEnums, OPTIONAL_EXTRAS_MONTHLY_COST_DISCLAIMER } from '@acorn-portal/shared/src/utils/constants';
import { MapOutOptionsExcess } from '@acorn-portal/shared/src/utils/map-out-options-excess';

function findHighestCompulsoryDriverExcessAmount(drivers: DriverExcess[]): number {
	let highestCompulsoryAmount = 0;
	for (const driver of drivers) {
		if (driver.compulsoryAmount > highestCompulsoryAmount) {
			highestCompulsoryAmount = driver.compulsoryAmount;
		}
	}
	return highestCompulsoryAmount;
}

const HyperLink = styled('a')(({ theme }) => ({
	color: theme.palette.primary.main,
	'&:hover': {
		color: theme.palette.primary.light,
	},
	'&:visited': {
		color: theme.palette.primary.dark,
		'&:hover': {
			color: theme.palette.primary.light,
		},
	},
}));

interface IProps {
	addOns: AddOn[];
	onSelect: (selectedAddOns: SelectedAddOn[]) => void;
	onRemove: (underwrittenDataPointId: string) => void;
}

const AddOnAdjustment = (current: SelectedAddOn[], options: AddOn[]) => {
	const temp = current.filter(
		(x) => !options.some((option) => option.underwrittenDataPointId === x.underwrittenDataPointId),
	);
	return temp;
};

const ExcessReimbursement: FC<IProps> = ({ addOns, onSelect, onRemove }) => {
	const [selectedOption, setSelectedOption] = useState<AddOn>();
	const renewalStore = useAppSelector((redux) => redux.renewal);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const paymentInFull = renewalStore.paymentOptions.method === 'total';

	const compulsoryExcess = useMemo(
		() => renewalStore.renewalRatingResponse?.coverDetails?.compulsoryExcess as number,
		[renewalStore],
	);

	const voluntaryExcess = renewalStore.renewalRatingResponse?.coverDetails?.voluntaryExcess as number;

	const driverExcess = findHighestCompulsoryDriverExcessAmount(
		renewalStore.renewalRatingResponse?.coverDetails?.driverExcesses ?? [],
	);

	const totalExcess = formatCurrency(compulsoryExcess + voluntaryExcess + driverExcess);

	const options = useMemo(() => {
		const tempOptions = addOns.filter((addon) => addon.addOnDescription.indexOf('Reimbursement') !== -1);

		setSelectedOption(tempOptions.find((opt) => opt.selected));
		return tempOptions.sort((a, b) => a.annualCost - b.annualCost);
	}, [addOns]);

	const onChange = (value: string) => {
		const selectedAddOn = options.find((opt) => opt.underwrittenDataPointId === value);
		if (!selectedAddOn) return;
		const currentAddons = renewalStore.addons;
		const newAddonList = AddOnAdjustment(currentAddons, options);
		const newSelectedAddOns = [
			...newAddonList,
			{
				underwrittenDataPointId: selectedAddOn.underwrittenDataPointId,
				addOnCode: selectedAddOn.addOnCode,
			},
		];
		setSelectedOption(selectedAddOn);
		onSelect(newSelectedAddOns);
	};

	const xsReimbursementUrl = getDocument(
		fullPolicy.product,
		fullPolicy.isShortTermPolicy,
		addonDocumentEnums.XS_REIMBURSEMENT,
	);

	const xsReimbursementBookletUrl = getDocument(
		fullPolicy.product,
		fullPolicy.isShortTermPolicy,
		addonDocumentEnums.XS_REIMBURSEMENT_BOOKLET,
	);
	return (
		<Box
			sx={(theme) => ({
				backgroundColor: '#fff',
				borderRadius: 2,
				p: 2,
				mb: 3,
				'& .MuiOutlinedInput-root': {
					backgroundColor: theme.palette.background.paper,
				},
			})}
		>
			<Typography sx={{ mb: 1, fontWeight: 'bold' }}>Excess Reimbursement</Typography>{' '}
			<Typography sx={{ mb: 2, fontSize: '1rem' }}>
				In the event that you have a claim on your policy, you will need to pay your excess to the insurer. However,
				with this optional product we will refund you up to the amount you select.
			</Typography>
			<Typography sx={{ mb: 2, fontSize: '1rem' }}>The total policy excess for this quote is {totalExcess}</Typography>
			<InputLabel htmlFor='excess-reimbursement' sx={{ display: 'none' }}>
				Excess Reimbursement
			</InputLabel>{' '}
			<Dropdown
				id='excess-reimbursement'
				placeholder='Please Select'
				value={selectedOption?.underwrittenDataPointId ?? ''}
				onChange={(value) => onChange(value as string)}
				label=''
				data={MapOutOptionsExcess(options)}
			/>
			{selectedOption && (
				<>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							my: 1,
						}}
					>
						{paymentInFull ? (
							<Typography variant='h5' fontWeight='bold'>
								{formatCurrency(selectedOption.annualCost)}
							</Typography>
						) : (
							<Grid>
								<Typography variant='h5' fontWeight='bold'>
									{formatCurrency(selectedOption.monthlyCost)}
									{' per month'}
								</Typography>
							</Grid>
						)}

						<Button
							aria-label='remove-excess-reimbursement'
							variant='contained'
							startIcon={<DeleteIcon />}
							onClick={() => {
								onRemove(selectedOption.underwrittenDataPointId);
							}}
						>
							Remove
						</Button>
					</Box>
					<Typography variant='body2' sx={{ pt: 1, mb: '1rem', fontStyle: 'italic' }}>
						{OPTIONAL_EXTRAS_MONTHLY_COST_DISCLAIMER}
					</Typography>
				</>
			)}
			<Accordion
				sx={(theme) => ({
					backgroundColor: 'unset',
					borderRadius: 'unset',
					borderTopLeftRadius: 'unset',
					borderTopRightRadius: 'unset',
					boxShadow: 'unset',
					borderBottom: `1px solid ${theme.palette.grey[300]}`,

					'&:before': {
						backgroundColor: 'unset',
					},
					'&:last-of-type': {
						borderBottomLeftRadius: 'unset',
						borderBottomRightRadius: 'unset',
					},
				})}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='tell-me-more-content' id='tell-me-more-header'>
					<Typography>Tell me more</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Typography>
						This product meets the demands and needs of someone who requires cover to reduce the amount of their Excess
						they have to pay in respect of a claim.
					</Typography>
					<br />
					<Typography>
						Key exclusions:
						<ul>
							<li>a claim made under your motor insurance policy where the claim amount does not exceed the excess.</li>
							<br />
							<li>the excess payable for glass repair or replacement.</li>
						</ul>
					</Typography>
					<Typography>
						This is a consumer contract providing reimbursement for the excess that you are responsible for in respect
						of a claim arising as a result of an insured peril under your motor insurance policy.
					</Typography>
					<br />
					<Typography>
						Provided by Haven Insurance Company Limited
						<br />
						<span>
							Please refer to the Policy Booklet and Insurance Product Information Document for full terms and
							conditions.
						</span>
						<br />
						<br />
						<HyperLink href={xsReimbursementUrl} target='_blank' rel='noreferrer'>
							Excess Reimbursement IPID
						</HyperLink>
						<br />
						<HyperLink href={xsReimbursementBookletUrl} target='_blank' rel='noreferrer'>
							Excess Reimbursement Booklet{' '}
						</HyperLink>
					</Typography>
				</AccordionDetails>
			</Accordion>
		</Box>
	);
};

export default ExcessReimbursement;
